import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { Box, Tab, Tabs, ThemeProvider, Typography, useTheme } from '@mui/material';
import DOMPurify from 'dompurify';
import { MobileInfoPage } from 'probonio-shared-ui/component/infoPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MATERIAL_DARK_THEME } from '../../../../../../theme';
import { PREVIEW_WIDTH } from '../PreviewStepper';

interface Props {
  html: string | undefined;
  image: string;
  benefitTitle: string;
}

export const HelpPreview: React.FC<Props> = ({ html, image, benefitTitle }) => {
  const theme = useTheme();
  const [isDark, setIsDark] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>('');

  const previewTheme = isDark ? MATERIAL_DARK_THEME : theme;

  const { t } = useTranslation('customBenefitsModule');

  useEffect(() => {
    if (typeof image === 'string' || !image) {
      setImageUrl(image);
      return;
    }
    const objectUrl = URL.createObjectURL(image);
    setImageUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image, setImageUrl]);

  return (
    <Box display="flex" flexDirection="column" width={PREVIEW_WIDTH} height="100%">
      <Tabs
        value={isDark ? 1 : 0}
        onChange={(_, val) => setIsDark(val === 1)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          pb: 1,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            borderBottom: '2px solid transparent',
            '&.Mui-selected': { borderBottomColor: 'primary.main' },
          },
        }}
      >
        <Tab icon={<LightModeIcon sx={{ fontSize: 18 }} />} label={t('changeMode.lightMode')} iconPosition="start" />
        <Tab icon={<DarkModeIcon sx={{ fontSize: 18 }} />} label={t('changeMode.darkMode')} iconPosition="start" />
      </Tabs>
      <ThemeProvider theme={previewTheme}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="start"
          py={1}
          flex={1}
          sx={{ backgroundColor: t => t.palette.background.default, overflow: 'hidden' }}
        >
          <Box display="flex" position="relative" justifyContent="center" mx={1} my={2} px={2.5} textAlign="center">
            <ArrowBackIcon sx={{ position: 'absolute', left: 0, marginRight: '30px', color: 'text.primary' }} />
            <Typography sx={{ wordBreak: 'break-word', color: 'text.primary' }} variant="h2">
              {benefitTitle}
            </Typography>
          </Box>
          <Box overflow="auto" sx={{ backgroundColor: t => t.palette.background.default }}>
            <MobileInfoPage image={imageUrl}>
              {/* eslint-disable-next-line react/no-danger */}
              <Box
                dangerouslySetInnerHTML={{ __html: html ? DOMPurify.sanitize(html) : '' }}
                sx={{
                  color: 'text.primary',
                  '& p': { paddingTop: '4px', paddingBottom: '4px', margin: 0, minHeight: '1.5em' },
                  '& blockquote': { margin: 0, padding: 0, fontStyle: 'normal', borderLeft: 'none' },
                }}
              />
            </MobileInfoPage>
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
};
